import React, { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp,
  Info,
} from 'lucide-react';

/**
 * Bottleneck Badge
 * 
 * Compact badge showing bottleneck status.
 * Hover to see full details that would have been on the card.
 */
const BottleneckBadge = ({ 
  period = "mtd", 
  vertical, 
  customStart, 
  customEnd,
  className = "" 
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBottleneck = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let url = `/analytics/bottleneck`;
        const params = new URLSearchParams();
        
        // Build date range from period
        const now = new Date();
        let startDate = new Date();
        
        if (period === "7d") {
          startDate.setDate(now.getDate() - 7);
        } else if (period === "30d") {
          startDate.setDate(now.getDate() - 30);
        } else if (period === "mtd") {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        } else if (period === "ytd") {
          startDate = new Date(now.getFullYear(), 0, 1);
        } else if (period === "custom" && customStart && customEnd) {
          params.set("start_date", customStart);
          params.set("end_date", customEnd);
        }
        
        if (period !== "custom") {
          params.set("start_date", startDate.toISOString().slice(0, 10));
          params.set("end_date", now.toISOString().slice(0, 10));
        }
        
        if (vertical && vertical !== "all") {
          params.set("vertical", vertical);
        }
        
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
        
        const res = await api.get(url);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load");
      } finally {
        setLoading(false);
      }
    };
    
    fetchBottleneck();
  }, [period, vertical, customStart, customEnd]);

  const formatPercent = (val) => {
    if (val === null || val === undefined) return "—";
    return `${(val * 100).toFixed(0)}%`;
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "—";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatVariance = (val) => {
    if (val === null || val === undefined) return "—";
    const percent = val * 100;
    const sign = percent >= 0 ? "+" : "";
    return `${sign}${percent.toFixed(0)}%`;
  };

  if (loading || error || !data) {
    return null;
  }

  const { has_bottleneck, primary_bottleneck, all_stages } = data;

  // No bottleneck - show success state
  if (!has_bottleneck || !primary_bottleneck) {
    return (
      <HoverCard openDelay={100} closeDelay={100}>
        <HoverCardTrigger asChild>
          <Badge
            variant="outline"
            className={`cursor-default transition-colors bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30 ${className}`}
            data-testid="bottleneck-badge"
          >
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3" />
              <span className="text-xs">No Bottlenecks</span>
            </div>
          </Badge>
        </HoverCardTrigger>
        <HoverCardContent 
          side="bottom" 
          align="end"
          className="w-72 bg-slate-950 border-white/10 text-slate-100 p-4 z-50"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-emerald-500/20">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-medium text-emerald-400">No Active Bottlenecks</div>
              <div className="text-xs text-slate-400">All funnel stages within acceptable variance.</div>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  // Has bottleneck - show warning state
  const severity = primary_bottleneck.variance_percent < -0.25 ? "critical" : "warning";
  const badgeClass = severity === "critical" 
    ? "bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30"
    : "bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30";
  const iconColor = severity === "critical" ? "text-red-400" : "text-amber-400";

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Badge
          variant="outline"
          className={`cursor-default transition-colors ${badgeClass} ${className}`}
          data-testid="bottleneck-badge"
        >
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3" />
            <span className="text-xs">{primary_bottleneck.stage}</span>
          </div>
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent 
        side="bottom" 
        align="end"
        className="w-80 bg-slate-950 border-white/10 text-slate-100 p-4 z-50"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className={`p-2 rounded-full ${severity === "critical" ? "bg-red-500/20" : "bg-amber-500/20"}`}>
            <AlertTriangle className={`h-5 w-5 ${iconColor}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-200">Primary Constraint</span>
              <Badge 
                variant="outline" 
                className={severity === "critical" 
                  ? "bg-red-500/20 text-red-300 border-red-500/30 text-xs" 
                  : "bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs"
                }
              >
                {primary_bottleneck.stage}
              </Badge>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {primary_bottleneck.description}
            </div>
          </div>
        </div>
        
        {/* Metrics Grid */}
        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div>
            <div className="text-xs text-slate-500 mb-0.5">Actual</div>
            <div className="font-semibold text-slate-200">
              {formatPercent(primary_bottleneck.actual)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">Target</div>
            <div className="font-semibold text-slate-200">
              {formatPercent(primary_bottleneck.target)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">Variance</div>
            <div className={`font-semibold ${severity === "critical" ? "text-red-400" : "text-amber-400"}`}>
              {formatVariance(primary_bottleneck.variance_percent)}
            </div>
          </div>
        </div>
        
        {/* Revenue Impact */}
        {primary_bottleneck.estimated_revenue_impact > 0 && (
          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-3 w-3 text-emerald-400" />
              <span className="text-xs text-slate-400">If improved to target:</span>
              <span className="text-xs font-medium text-emerald-400">
                +{formatCurrency(primary_bottleneck.estimated_revenue_impact)} potential
              </span>
            </div>
          </div>
        )}
        
        {/* Other stages preview */}
        {all_stages && all_stages.length > 1 && (
          <div className="mt-3 pt-2 border-t border-white/10">
            <div className="text-xs text-slate-500 mb-2">All Stages</div>
            <div className="flex flex-wrap gap-1">
              {all_stages.map((stage) => (
                <Badge 
                  key={stage.stage_key}
                  variant="outline"
                  className={`text-[10px] ${
                    stage.variance_percent < -0.10 
                      ? "bg-red-500/10 text-red-300 border-red-500/20"
                      : stage.variance_percent < -0.05
                      ? "bg-amber-500/10 text-amber-300 border-amber-500/20"
                      : "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                  }`}
                >
                  {stage.stage}: {formatPercent(stage.actual)}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

export default BottleneckBadge;
